import React from "react";
import { useEffect, useRef, useState, memo,useCallback } from "react";
import { useSelector } from "react-redux";
//helper
import debounce from "../../helper/debounce";
import offset from "../../helper/offsetTop";
// components
import FacilityActivitiesGroupsList from "./facilityActivitiesGroupsList";
import FacilityNavigate from "./facilityNavigate";
import FacilityDiscount from "./facilityDiscount";
import FacilityCards from "./facilityCards";
import FacilityImages from "./facilityImages";
import FacilityTitle from "./facilityTitle";
import FacilityAddress from "./faciityAddress";
/**
 * A component that renders a list of facilities with their information and discounts.
 * @param {Object} props - The props object.
 * @param {Array} props.options - An array of facility objects to be rendered.
 * @param {string} props.visible - A string indicating whether the component is visible or not.
 * @param {string} props.scrollTo - A string indicating the facility ID to scroll to.
 * @returns {JSX.Element} - The FacilitiesList component.
 */
const FacilitiesList = memo((props) => {
	const [activeRef, setActiveRef] = useState(null);
	const listEl = useRef(null);
	const refs = useRef({});
	const global = useSelector((state) => state.global.localization);
	const searchData = useSelector((state) => state.searchData);
	if (props.options) {
		refs.current = props.options.reduce((acc, value) => {
			acc[value.facilityId] = React.createRef(null);
			return acc;
		}, {});
	}
	useEffect(() => { 
		calculateOffset();
	},[]);
	useEffect(() => {
		if(!props.toggleMap){
			calculateOffset();
		}
	}, [props.toggleMap]);
	useEffect(() => {
		const debouncedHandleResize = debounce(function handleResize() {
			calculateOffset();
		}, 500);

		window.addEventListener("resize", debouncedHandleResize);
		return (_) => {
			window.removeEventListener("resize", debouncedHandleResize);
		};
	});
	// Scroll to element list when pin is clicked
	useEffect(() => {
		if (props.scrollTo !== null) {
			listEl.current.scrollTo({
					top:
					listEl.current.scrollHeight +
					refs.current[props.scrollTo].current.offsetTop -
					listEl.current.scrollHeight
				})

			if (activeRef instanceof HTMLElement) {
				activeRef.classList.remove("is-active");
			}
			refs.current[props.scrollTo].current.classList.add("is-active");
			setActiveRef(refs.current[props.scrollTo].current);
		} else {
			if (activeRef instanceof HTMLElement) {
				activeRef.classList.remove("is-active");
			}
			setActiveRef(null);
		}
	}, [props.scrollTo]);
	const calculateOffset = ()=>{
		const  list = listEl.current;
		const windowHeight = document.querySelector('html').getBoundingClientRect().height;
		if (list !== null) {
			document.documentElement.style.setProperty(
				"--scroll-height",
				windowHeight-offset(list).top.toFixed() + "px",
			);
		}
	};
	return (
		<div className="info-block-list__results" ref={listEl}>
			<ul
				className={`info-block-list ${
					props.options.length === 1 ? "has-single-res" : ""
				}`}
				>
				{props.options.map((item) => {
					return (
						<li
							id={item.facilityId}
							key={item.facilityId}
							ref={refs.current[item.facilityId]}
							className={`info-block-list__item ${
								item.discounts.length ? "has-discount" : ""
							}`}>
							{item.discounts.length > 0 ? (
								<FacilityDiscount
									item={item}
									localization={global}
								/>
							) : null}
							<FacilityImages
								photos={item.photos}
								name={item.name}
								defaultImg={global.defaultImageUrl}
							/>
							<div className="info-block-list__content">
								<div className="info-block-list__main-info">
									<FacilityTitle
										name={item.name}
										slug={item.slug}
									/>
									<FacilityAddress
										city={item.city}
										address={item.street}
									/>
									<FacilityNavigate
										distance={{
											current: item.distance,
											lat: item.latitude,
											lng: item.longitude,
										}}
										global={global}
										location={searchData.location}
									/>
									{item.cards.length ? (
										<FacilityCards cards={item.cards} />
									) : null}
								</div>
								{item.activityGroups && item.activityGroups.length ? (
									<FacilityActivitiesGroupsList options={item.activityGroups} />
								) : null}
								{!item.activityGroups && item.discounts.length > 0 ? (
									<span className="info-block-list__helper">
										{global.facilityDiscounts}
									</span>
								) : null}
							</div>
						</li>
					);
				})}
			</ul>
		</div>
	);
});

export default FacilitiesList;
